import { useEffect, useMemo, useRef, useState } from 'react'

import { useAppDataContext } from '@cox/core-ui8/dist/AppDataContext'
import { useHeaderProfileContext } from '@cox/core-ui8/dist/HeaderProfileContext'
import Image, { ImageLinkTypes } from '@cox/core-ui8/dist/Image'
import { handleChatInfo, useOliverChat } from '@cox/core-ui8/dist/useOliverChat'

import { ReactImageProps } from './types'
import withAEM from '../../hoc/withAEM'
import { useInversionData } from '../../hooks/useInversionData'
import { appendHash } from '../../utils/helper-util'
import { getInversionTokenValues } from '../../utils/inversion-util'
import { getImageSrc } from '../../utils/local-util'
import { handleModal } from '../../utils/modal-util'
import { extractText, getTokenValues } from '../../utils/rule-builder-util'

const ReactImage = (props: ReactImageProps): JSX.Element => {
  const {
    alt = '',
    src = '',
    caption = undefined,
    displayPopupTitle = false,
    tokenProperties = '',
    token = '',
    imageHeight = 0,
    linkId = '',
    linkType = '',
    linkUrl = '',
    imageWidth = 0,
    imageUnit = 'px',
    ariaLabel = '',
    chatText = '',
    phoneNo = '',
    dmPresetType = '',
    imagePreset = ''
  } = props

  const ImageWithAEM = withAEM(Image)

  const inversionItems = useInversionData()
  const newItems = useMemo(() => inversionItems, [inversionItems])
  const { appData } = useAppDataContext()
  const isLocal = appData?.page?.isLocal
  const invTokenregex = /\[\[(?!token|offer)[^\]]+\]\]/g
  let inversionToken = token
  let inversionAlt = alt

  if (isLocal) {
    if (invTokenregex.test(token) || invTokenregex.test(alt)) {
      inversionToken = getInversionTokenValues(token, newItems) || ''
      inversionAlt = getInversionTokenValues(alt, newItems) || ''

      if (dmPresetType === 'imagePreset' && imagePreset) {
        inversionToken += `?$${imagePreset}$`
      }
    }
  }

  const { headerProfileRes } = useHeaderProfileContext()
  const [imageFields, setImageFields] = useState({
    tokenSrc: '',
    tokenCaption: '',
    tokenAlt: '',
    tokenAriaLabel: '',
    tokenAnchor: '',
    imageUrl: ''
  })
  const imageRef = useRef<any>(null)
  const { isChatLoaded, options } = useOliverChat()

  useEffect(() => {
    const imgContainer = imageRef?.current
    if (linkType === ImageLinkTypes.MODAL) {
      if (imgContainer) {
        imgContainer?.parentNode?.addEventListener('click', handleModal, false)
      }
    } else if (linkType === ImageLinkTypes.CHAT) {
      handleChatInfo(imgContainer, options, isChatLoaded, chatText)
    }
  }, [linkType, isChatLoaded])

  const getImageSource = (tokenSrc: string) => {
    if (typeof tokenSrc === 'string' && tokenSrc.trim() !== '') {
      if (tokenSrc.includes('https')) {
        return tokenSrc
      } else {
        return getImageSrc(tokenSrc)
      }
    }

    return ''
  }

  useEffect(() => {
    const tokenSrc = token ? (getTokenValues(inversionToken, tokenProperties) as string) : src
    const tokenCaption = getTokenValues(caption, tokenProperties) as string
    const tokenAlt = getTokenValues(inversionAlt, tokenProperties) as string
    const tokenAriaLabel = getTokenValues(ariaLabel, tokenProperties) as string
    const tokenAnchor = getTokenValues(linkId, tokenProperties) as string
    const imageUrl = getImageSource(tokenSrc)

    setImageFields({
      tokenSrc,
      tokenCaption,
      tokenAlt,
      tokenAriaLabel,
      tokenAnchor,
      imageUrl
    })
  }, [headerProfileRes])

  const {
    tokenCaption = '',
    tokenAlt = '',
    tokenAnchor = '',
    tokenAriaLabel = '',
    imageUrl = '',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    tokenSrc = ''
  } = imageFields
  const altText = displayPopupTitle && tokenCaption ? tokenCaption : tokenAlt
  const ariaLabelTxt = displayPopupTitle && tokenCaption ? tokenCaption : tokenAriaLabel
  const imageTooltip = altText?.length > 0 ? extractText(altText) : altText.toString()
  const height = imageHeight > 0 ? `${imageHeight}${imageUnit}` : undefined
  const width = imageWidth > 0 ? `${imageWidth}${imageUnit}` : undefined
  const navigateUrl = linkType === ImageLinkTypes.PHONE ? phoneNo : appendHash(linkUrl, tokenAnchor ?? '')
  const anchorCss = navigateUrl === '#' || navigateUrl === '' ? 'default' : ''

  return (
    <ImageWithAEM
      {...props}
      alt={altText}
      src={imageUrl}
      caption={tokenCaption}
      height={height}
      width={width}
      linkUrl={navigateUrl}
      linkAlt={imageTooltip}
      linkCss={anchorCss}
      ariaLabel={ariaLabelTxt}
      compName={'Image'}
    />
  )
}

export default ReactImage
